import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import axios from "axios";
import { captureException } from "@sentry/nextjs";
import { getStudentFromStorage } from "@helpers/migrateLogin";

/**
 * Hook to handle referral invite acceptance
 * Looks for 'pwr' (profile_with_region) query parameter
 * Format: pwr=67250_eu where 67250 is referrer profile_id and eu is region
 */
export const useReferralInvite = () => {
    const router = useRouter();
    const hasProcessedRef = useRef(false);

    useEffect(() => {
        // Only run once and only on client side
        if (typeof window === "undefined" || hasProcessedRef.current) {
            return;
        }

        const processPwrParam = async (is_new_user: boolean) => {
            try {
                const { pwr } = router.query;

                // Check if pwr parameter exists
                if (!pwr || typeof pwr !== "string") {
                    return;
                }

                // Skip processing on /pg/refer_and_earn page - this is the landing page itself
                if (router.pathname === "/pg/refer_and_earn") {
                    console.log(
                        "[useReferralInvite] On referral landing page, skipping processing",
                    );
                    return;
                }

                // Skip processing on /profile page - user needs to select a profile first
                if (router.pathname === "/profile") {
                    localStorage.setItem("pending_referral_pwr", pwr);
                    console.log(
                        "[useReferralInvite] On profile selection page, stored pwr for later",
                    );
                    return;
                }

                // Check if already processing to prevent duplicate calls
                const isProcessing = localStorage.getItem(
                    "referral_processing",
                );
                if (isProcessing === "true") {
                    console.log(
                        "[useReferralInvite] Already processing, skipping",
                    );
                    return;
                }

                // Check if user just navigated from profile selection (isNavigated flag)
                // If not set, it means user is being redirected to /profile and shouldn't process yet
                const isNavigated = sessionStorage.getItem("isNavigated");
                if (!isNavigated) {
                    // User hasn't selected a profile yet, store pwr for later
                    localStorage.setItem("pending_referral_pwr", pwr);
                    console.log(
                        "[useReferralInvite] User not yet navigated from profile, stored pwr for later",
                    );
                    return;
                }

                // Mark as processing to prevent duplicate calls
                localStorage.setItem("referral_processing", "true");

                // Parse pwr parameter (format: profileId_region)
                const pwrParts = pwr.split("_");
                if (pwrParts.length !== 2) {
                    console.error(
                        "[useReferralInvite] Invalid pwr format:",
                        pwr,
                    );
                    return;
                }

                const [referrerProfileId, referrerRegion] = pwrParts;

                // Get current user from storage
                const studentData = getStudentFromStorage();
                if (!studentData) {
                    // User not logged in - store pwr in localStorage for later
                    localStorage.setItem("pending_referral_pwr", pwr);
                    console.log(
                        "[useReferralInvite] User not logged in, stored pwr for later",
                    );
                    return;
                }

                const student = JSON.parse(studentData);
                const other = JSON.parse(student?.other ?? "{}");
                const { id: referredProfileId, mobile, name } = student;
                const referredRegion = process.env.NEXT_PUBLIC_SERVER_REGION;
                const profile_picture = other?.profile_picture;

                if (!referredProfileId || !mobile || !referredRegion || !name) {
                    console.error(
                        "[useReferralInvite] Missing required user data",
                    );
                    return;
                }

                // Mark as processed to prevent duplicate calls
                hasProcessedRef.current = true;

                // Call acceptInvite API
                const response = await axios.post(
                    `${process.env.NEXT_PUBLIC_MONOLITH_ENDPOINT}/v3/personalizedLearning/acceptInvite`,
                    {
                        referrer: {
                            profile_id: referrerProfileId,
                            region: referrerRegion,
                        },
                        referred: {
                            profile_id: String(referredProfileId),
                            mobile,
                            region: referredRegion,
                            name,
                            profile_picture: profile_picture || "",
                            is_new_user,
                        },
                    },
                );

                if (response.data?.success) {
                    console.log(
                        "[useReferralInvite] Invite accepted successfully",
                    );

                    // Clear pending referral from localStorage
                    localStorage.removeItem("pending_referral_pwr");
                    // Clear processing flag
                    localStorage.removeItem("referral_processing");

                    // Remove pwr from URL without page reload
                    const newQuery = { ...router.query };
                    delete newQuery.pwr;
                    router.replace(
                        {
                            pathname: router.pathname,
                            query: newQuery,
                        },
                        undefined,
                        { shallow: true },
                    );
                } else {
                    console.error(
                        "[useReferralInvite] Failed to accept invite:",
                        response.data?.message,
                    );
                    // Clear processing flag on failure
                    localStorage.removeItem("referral_processing");
                }
            } catch (error: any) {
                console.error(
                    "[useReferralInvite] Error processing referral:",
                    error,
                );
                captureException(error);

                // Clear processing flag on error
                localStorage.removeItem("referral_processing");

                // If it's a known error (already referred, etc.), clear the pending referral
                if (error?.response?.status === 400) {
                    localStorage.removeItem("pending_referral_pwr");

                    // Remove pwr from URL
                    const newQuery = { ...router.query };
                    delete newQuery.pwr;
                    router.replace(
                        {
                            pathname: router.pathname,
                            query: newQuery,
                        },
                        undefined,
                        { shallow: true },
                    );
                }
            }
        };

        // Only process if router is ready
        if (router.isReady) {
            processPwrParam(false);
        }
    }, [router.isReady, router.query]);
};

/**
 * Hook to process pending referral after login
 * Should be called after successful login/signup
 */
export const useProcessPendingReferral = () => {
    const processPendingReferral = async (is_new_user: boolean) => {
        try {
            const pendingPwr = localStorage.getItem("pending_referral_pwr");
            if (!pendingPwr) {
                return;
            }

            // Check if already processing to prevent duplicate calls
            const isProcessing = localStorage.getItem("referral_processing");
            if (isProcessing === "true") {
                console.log(
                    "[useProcessPendingReferral] Already processing, skipping",
                );
                return;
            }

            // Parse pwr parameter
            const pwrParts = pendingPwr.split("_");
            if (pwrParts.length !== 2) {
                console.error(
                    "[useProcessPendingReferral] Invalid pwr format:",
                    pendingPwr,
                );
                localStorage.removeItem("pending_referral_pwr");
                return;
            }

            const [referrerProfileId, referrerRegion] = pwrParts;

            // Mark as processing and clear pending referral BEFORE making API call to prevent duplicate calls
            localStorage.setItem("referral_processing", "true");
            localStorage.removeItem("pending_referral_pwr");

            // Get current user from storage
            const studentData = getStudentFromStorage();
            if (!studentData) {
                console.error(
                    "[useProcessPendingReferral] User not found after login",
                );
                localStorage.removeItem("referral_processing");
                return;
            }

            const student = JSON.parse(studentData);
            const other = JSON.parse(student?.other ?? "{}");
            const { id: referredProfileId, mobile, name } = student;
            const referredRegion = process.env.NEXT_PUBLIC_SERVER_REGION;
            const profile_picture = other?.profile_picture;

            if (!referredProfileId || !mobile || !referredRegion || !name) {
                console.error(
                    "[useProcessPendingReferral] Missing required user data",
                );
                localStorage.removeItem("referral_processing");
                return;
            }

            // Call acceptInvite API
            const response = await axios.post(
                `${process.env.NEXT_PUBLIC_MONOLITH_ENDPOINT}/v3/personalizedLearning/acceptInvite`,
                {
                    referrer: {
                        profile_id: referrerProfileId,
                        region: referrerRegion,
                    },
                    referred: {
                        profile_id: String(referredProfileId),
                        mobile,
                        region: referredRegion,
                        name,
                        profile_picture:
                            profile_picture ||
                            "https://sets-gamify-assets.s3.ap-south-1.amazonaws.com/math-ai-assets/assets/avatars/male/5.png",
                        is_new_user,
                    },
                },
            );

            if (response.data?.success) {
                console.log(
                    "[useProcessPendingReferral] Pending invite accepted successfully",
                );
                // Clear processing flag on success
                localStorage.removeItem("referral_processing");
            } else {
                // Clear processing flag on failure
                localStorage.removeItem("referral_processing");
            }
        } catch (error: any) {
            console.error(
                "[useProcessPendingReferral] Error processing pending referral:",
                error,
            );
            captureException(error);

            // Clear processing flag on error
            localStorage.removeItem("referral_processing");

            // Clear pending referral on error to prevent infinite retries
            if (error?.response?.status === 400) {
                localStorage.removeItem("pending_referral_pwr");
            }
        }
    };

    return { processPendingReferral };
};
